// Order void / refund — the backend's /api/merchant/orders/{id}/void|refund
// (backend/payments/refunds.py). A card void / refund goes through Aster
// Gateway: the request answers 202 while the gateway works, and the result is
// polled until its verdict. Nothing is shown as refunded before that verdict.
import axios from 'axios';
import { API } from '@/config/api';

const POLL_EVERY_MS = 2000;
const POLL_FOR_MS = 120000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function errorText(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  // a table paid by check: the answer names its checks (refund them in Dining > Checkout > Closed checks)
  if (detail && typeof detail.message === 'string') return detail.message;
  return fallback;
}

export async function fetchRefundSummary(orderId, headers) {
  const response = await axios.get(`${API}/merchant/orders/${orderId}/refunds`, headers);
  return response.data;
}

async function untilFinal(orderId, first, headers) {
  let view = first;
  const deadline = Date.now() + POLL_FOR_MS;
  while (view && view.is_final === false && Date.now() < deadline) {
    await sleep(POLL_EVERY_MS);
    const response = await axios.get(`${API}/merchant/orders/${orderId}/reversals/${view.payment_id}`, headers);
    view = response.data;
  }
  return view;
}

// Resolves with the final view ({status: 'approved', ...}), or a view that is
// still open after the wait (status not final: the gateway has not answered
// yet — never treated as done). Rejects with the server's reason otherwise.
async function run(orderId, path, body, headers) {
  const response = await axios.post(`${API}/merchant/orders/${orderId}/${path}`, body, headers);
  return untilFinal(orderId, response.data, headers);
}

export function voidOrderPayment(orderId, { reason, notes }, headers) {
  return run(orderId, 'void', { reason, notes }, headers);
}

export function refundOrderPayment(orderId, { amountCents, reason, notes, refundedCentsBefore }, headers) {
  return run(orderId, 'refund', {
    amount_cents: amountCents,
    reason,
    notes,
    refunded_cents_before: refundedCentsBefore,
  }, headers);
}

// One toast-ready sentence for a finished (or still open) void / refund.
export function outcomeText(view, orderId) {
  if (!view) return `Order #${orderId}: no answer`;
  if (view.is_final === false) {
    return `Order #${orderId}: Aster Gateway has not answered yet — check the order again in a moment`;
  }
  if (view.status === 'approved') {
    if (view.tender && view.message) return view.message; // cash / manual: hand it back
    const dollars = ((view.amount_cents || 0) / 100).toFixed(2);
    return view.kind === 'void' ? `Order #${orderId} voided` : `Refunded $${dollars} for Order #${orderId}`;
  }
  return view.message || `Order #${orderId}: not completed`;
}

export function toCents(text) {
  const value = Number.parseFloat(text);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
}
